<template>
  <div class="stake-area">
    <WindowModal :title="modalTitle" type="Global">
      <template #modal-content>
        <StakeAsset
          v-if="currentPool !== null"
          ref="stakeAsset"
          :pool="currentPool"
          :action="actionPicked"
          :transaction="transaction"
          @stake="stakePool"
          @unstake="unstakePool"
          @extend="extendLocked"
          @initConfirmState="changeModalTitle('init')"
          @initLoading="changeModalTitle('loading')"
          @initSuccess="changeModalTitle('success')"
          @initError="changeModalTitle('error')"
          @closeGlobalModal="closeModal"
        />
      </template>
    </WindowModal>
    <RoiCalculator
      v-if="currentStake !== null"
      :token="currentStake.token"
      :deposited="currentStakeDepend.deposited"
      :apr="currentStakeDepend.apr"
      :type="currentStakeDepend.type"
      :balance="currentStakeDepend.balance"
      :performance-fee="currentStakeDepend.performanceFee"
    />
    <div class="stake-box">
      <div class="text-area">
        <h1>
          {{ $t('stake.stake') }}
        </h1>
        <p class="desc">
          {{ $t('stake.subheader1') }}
        </p>
        <p class="desc">
          {{ $t('stake.subheader2') }}
        </p>
      </div>
      <div class="image-area">
        <div class="picture">
          <picture>
            <source srcset="/img/new/stake_hand.webp" type="image/webp">
            <nuxt-img
              fit="inside"
              width="919"
              sizes="sm:300px md:400px lg:500px xl:600px xxl:700px"
              src="/img/new/stake_hand.png"
              alt="Farm $FLOATIE and harvest coin rewards on Defi Pool Finance"
              title="Farm $FLOATIE"
            />
          </picture>
        </div>
      </div>
      <div class="cyan-cloud">
        <CloudCyanStakeLeft />
      </div>
      <div class="cyan-cloud2">
        <CloudCyanStakeRight />
      </div>
    </div>
    <div class="filter-row">
      <div class="start-area">
        <div class="switch-area">
          <Switcher id="stakedOnly" v-model="filter.stakedOnly" :label="$t('stake.staked_only')" @select="changeStakedOnly" />
        </div>
        <div class="status-area">
          <button type="button" :class="{'btn-status': true, active: filter.status === 'live'}" @click.prevent="setStatus('live')">
            {{ $t('stake.live') }}
          </button>
          <button type="button" :class="{'btn-status': true, active: filter.status === 'finished'}" @click.prevent="setStatus('finished')">
            {{ $t('stake.finished') }}
          </button>
        </div>
      </div>
      <div class="end-area">
        <div class="sort-area">
          <label for="sortList">
            {{ $t('common.sort_by') }}
          </label>
          <Select v-model="filter.sort" :options="filterOptions" />
        </div>
        <div class="search-area">
          <div class="search-input">
            <Search />
            <input v-model="filter.search" type="text" сlass="input" :placeholder="$t('common.search') + '...'">
          </div>
        </div>
      </div>
    </div>
    <div v-if="lockedPool !== null && !lockedPoolLoading" class="pools-area">
      <div v-if="lockedPool !== null" class="pool-item">
        <div ref="poolHeader999" :class="{'pool-header': true, 'active': lockedPool.opened}" @click="toggleAccordion(999)">
          <div class="logo">
            <nuxt-img
              placeholder
              src="/img/new/floatie_item.svg"
              alt="$FLOATIE stake pool for earning auto flexible rewards"
              title="$FLOATIE stake pool"
              loading="lazy"
            />
            <div class="refresh-icon">
              <Refresh />
            </div>
          </div>
          <div class="header-area">
            <div class="h4">
              {{ $t('stake.locked_stake') }} {{ $config.floatieName }}
            </div>
            <p class="desc">
              {{ $t('stake.pool_desc') }}
            </p>
          </div>
          <div class="regular-item stacked-area no-mobile-border">
            <div class="icon-area">
              <Cube />
            </div>
            <div class="value-area">
              <p class="desc">
                {{ $t('stake.earned') }}
              </p>
              <p class="value">
                {{ $formatCryptoAmount(lockedPool.earned, 6) + ' ' + $config.floatieSymbol }} / {{ convertToUsd(lockedPool.earned) }} USD
              </p>
            </div>
          </div>
          <div class="regular-item apy-area">
            <div class="icon-area">
              <Cubes />
            </div>
            <div class="value-area">
              <p class="desc">
                {{ $t('stake.locked') }}
              </p>
              <p v-if="lockedPool.info.floatieAtLastUserAction !== -1" class="value">
                {{ $formatCryptoAmount(lockedPool.info.floatieAtLastUserAction) + ' ' + $config.floatieSymbol }}
              </p>
              <div v-else class="value">
                <Skeleton width="60px" height="16px" />
              </div>
            </div>
          </div>
          <div class="regular-item apr-area">
            <div class="icon-area">
              <Cubes />
            </div>
            <div class="value-area">
              <p class="desc">
                {{ $t('stake.locked') + ' ' + $t('stake.apy') }}
              </p>
              <p v-if="lockedPool.apy !== -1" class="value">
                <span>
                  {{ lockedPool.apy > 1000 ? $getBigNumber(lockedPool.apy) : $priceFormat(lockedPool.apy, 2) }}%
                </span>
                <span class="roi-icon" @click="openRoiCalculator(lockedPool)">
                  <Calculator />
                </span>
              </p>
              <div v-else class="value">
                <Skeleton width="60px" height="16px" />
              </div>
            </div>
          </div>
          <div class="regular-item total-area">
            <div class="icon-area">
              <Cubes />
            </div>
            <div class="value-area">
              <p class="desc">
                {{ $t('stake.total_locked') }}
              </p>
              <p class="value">
                {{ (lockedPool.totalLocked > 1000 ? $priceFormat(lockedPool.totalLocked, 0) : $priceFormat(lockedPool.totalLocked, 2)) + ' ' + $config.floatieSymbol }}
              </p>
            </div>
          </div>
          <div class="trigger-area">
            <p>
              {{ !lockedPool.opened ? $t('common.show') : $t('common.hide') }}
              <ChevronDown />
            </p>
          </div>
        </div>
        <div ref="poolDetails999" :class="{'pool-details': true, 'active': lockedPool.opened}">
          <div class="stake-container">
            <div class="desc-area">
              <div class="desc-row avg-lock">
                <p class="desc">
                  {{ $t('stake.avg_lock_duration') }}:
                </p>
                <p class="value">
                  {{ '1-52 ' + $t('common.weeks') }}
                </p>
              </div>
              <div class="desc-row performance">
                <p class="desc">
                  {{ $t('stake.performance_fee') }}:
                </p>
                <p class="value">
                  0-{{ $priceFormat(lockedPool.performanceFee, 0) + '%' }}
                </p>
              </div>
              <div class="desc-row status">
                <div class="status-bar auto">
                  <Refresh />
                  <span>
                    {{ $t('stake.auto') }}
                  </span>
                </div>
              </div>
            </div>
            <div class="numbers-area">
              <div class="input-area recent-profit">
                <div class="info">
                  <div class="h6">
                    {{ $t('stake.recent') + ' ' + $config.floatieName + ' ' + $t('stake.profit') }}
                  </div>
                  <div class="h3">
                    {{ $formatCryptoAmount(lockedPool.earned, 6) }}
                    <span class="currency">
                      {{ $config.floatieSymbol }}
                    </span>
                  </div>
                  <div class="h5">
                    {{ convertToUsd(lockedPool.earned) }}
                    <span class="currency">
                      USD
                    </span>
                  </div>
                </div>
                <div v-if="lockedPool.info.lockedAmount > 0" class="info">
                  <div class="h6">
                    {{ $t('stake.unlocks') }}
                  </div>
                  <div class="h3">
                    {{ unlocksRealDate() }}
                  </div>
                  <div class="h5">
                    {{ $t('common.on') + ' ' + $beautifyDate(parseInt(lockedPool.info.lockEndTime, 10) * 1000) }}
                  </div>
                </div>
              </div>
              <div v-if="!lockedPeriodEnded() || lockedPool.info.locked === false" class="buttons-area">
                <button type="button" :class="{'btn cyan': true, 'loading': stakeLoading, 'disabled': stakeLoading}" @click="prepareStaking(lockedPool)">
                  {{ lockedPool.info.locked === true && lockedPool.info.lockedAmount > 0 ? $t('stake.add_floatie') : $t('stake.stake') }}
                </button>
                <button v-if="lockedPool.info.lockedAmount > 0" type="button" :class="{'btn cyan': true, 'loading': stakeLoading, 'disabled': stakeLoading}" @click="prepareExtend(lockedPool)">
                  {{ $t('stake.extend') }}
                </button>
              </div>
              <div v-else-if="lockedPool.info.locked === true" class="info-area">
                <div class="icon-area">
                  <WarnCircleIcon />
                  <p class="desc">
                    {{ $t('stake.lock_can_be_burned') }}
                  </p>
                </div>
                <div class="buttons-area">
                  <button type="button" :class="{'btn cyan': true, 'loading': stakeLoading, 'disabled': stakeLoading}" @click="prepareExtend(lockedPool)">
                    {{ $t('stake.renew') }}
                  </button>
                  <button type="button" :class="{'btn cyan btn-outline': true, 'loading': stakeLoading, 'disabled': stakeLoading}" @click="unstakeLocked(lockedPool.info.lockedAmount)">
                    <span class="text">
                      {{ $t('stake.unstake') }}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="help-area">
            <div class="help-list">
              <div class="item">
                <a class="bottom-link" :href="generateTokenInfoLink({address: $config.periodToken})" rel="nofollow noopener noreferrer" target="_blank">
                  <ChevronRight />
                  {{ $t('stake.token_info') }}
                </a>
              </div>
              <div class="item">
                <nuxt-link class="bottom-link" :to="localePath('/docs')">
                  <ChevronRight />
                  {{ $t('stake.view_tutorial') }}
                </nuxt-link>
              </div>
              <div class="item">
                <a class="bottom-link" :href="generateScanLink({address: $config.periodVault})" rel="nofollow noopener noreferrer" target="_blank">
                  <ChevronRight />
                  {{ $t('stake.view_contract') }}
                </a>
              </div>
              <div class="item">
                <p class="bottom-link" @click="$addToMetamask(lockedPool.token)">
                  <ChevronRight />
                  {{ $t('common.add_to_metamask') }}
                  <MetamaskIcon />
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="pool-item">
        <div class="pool-header">
          <div class="logo">
            <Skeleton width="42px" height="42px" circle />
          </div>
          <div class="header-area">
            <Skeleton width="150px" height="14px" style="margin-bottom: .25rem" />
            <Skeleton width="175px" height="18px" />
          </div>
          <div class="regular-item no-mobile-border">
            <div class="icon-area">
              <Skeleton width="21px" height="21px" />
            </div>
            <div class="value-area">
              <Skeleton width="45px" height="14px" style="margin-bottom: .25rem" />
              <Skeleton width="100px" height="18px" />
            </div>
          </div>
          <div class="regular-item">
            <div class="icon-area">
              <Skeleton width="21px" height="21px" />
            </div>
            <div class="value-area">
              <Skeleton width="45px" height="14px" style="margin-bottom: .25rem" />
              <Skeleton width="100px" height="18px" />
            </div>
          </div>
          <div class="regular-item">
            <div class="icon-area">
              <Skeleton width="21px" height="21px" />
            </div>
            <div class="value-area">
              <Skeleton width="45px" height="14px" style="margin-bottom: .25rem" />
              <Skeleton width="100px" height="18px" />
            </div>
          </div>
          <div class="regular-item">
            <div class="icon-area">
              <Skeleton width="21px" height="21px" />
            </div>
            <div class="value-area">
              <Skeleton width="45px" height="14px" style="margin-bottom: .25rem" />
              <Skeleton width="100px" height="18px" />
            </div>
          </div>
          <div class="trigger-area">
            <Skeleton width="50px" height="18x" />
          </div>
        </div>
      </div>
      <div v-if="flexiblePool !== null" class="pool-item">
        <div ref="poolHeader1000" :class="{'pool-header': true, 'active': flexiblePool.opened}" @click="toggleAccordion(1000)">
          <div class="logo">
            <nuxt-img
              placeholder
              src="/img/new/floatie_item.svg"
              alt="$FLOATIE stake pool for earning auto flexible rewards"
              title="$FLOATIE stake pool"
              loading="lazy"
            />
            <div class="refresh-icon">
              <Refresh />
            </div>
          </div>
          <div class="header-area">
            <div class="h4">
              {{ $t('stake.flexible_stake') }} {{ $config.floatieName }}
            </div>
            <p class="desc">
              {{ $t('stake.pool_desc') }}
            </p>
          </div>
          <div class="regular-item stacked-area no-mobile-border">
            <div class="icon-area">
              <Cube />
            </div>
            <div class="value-area">
              <p class="desc">
                {{ $t('stake.earned') }}
              </p>
              <p class="value">
                {{ $formatCryptoAmount(flexiblePool.earned, 6) + ' ' + $config.floatieSymbol }} / {{ convertToUsd(flexiblePool.earned) }} USD
              </p>
            </div>
          </div>
          <div class="regular-item apy-area">
            <div class="icon-area">
              <Cubes />
            </div>
            <div class="value-area">
              <p class="desc">
                {{ $t('stake.locked') }}
              </p>
              <p v-if="flexiblePool.info.lockedAmount !== -1" class="value">
                {{ $formatCryptoAmount(flexiblePool.info.lockedAmount) + ' ' + $config.floatieSymbol }}
              </p>
              <div v-else class="value">
                <Skeleton width="60px" height="16px" />
              </div>
            </div>
          </div>
          <div class="regular-item apr-area">
            <div class="icon-area">
              <Cubes />
            </div>
            <div class="value-area">
              <p class="desc">
                {{ $t('stake.locked') + ' ' + $t('stake.apy') }}
              </p>
              <p v-if="flexiblePool.apy !== -1" class="value">
                <span>
                  {{ flexiblePool.apy > 1000 ? $getBigNumber(flexiblePool.apy) : $priceFormat(flexiblePool.apy, 2) }}%
                </span>
                <span @click="openRoiCalculator(flexiblePool)">
                  <Calculator />
                </span>
              </p>
              <div v-else class="value">
                <Skeleton width="60px" height="16px" />
              </div>
            </div>
          </div>
          <div class="regular-item total-area">
            <div class="icon-area">
              <Cubes />
            </div>
            <div class="value-area">
              <p class="desc">
                {{ $t('stake.total_locked') }}
              </p>
              <p class="value">
                {{ (flexiblePool.totalLocked > 1000 ? $priceFormat(flexiblePool.totalLocked, 0) : $priceFormat(flexiblePool.totalLocked, 2)) + ' ' + $config.floatieSymbol }}
              </p>
            </div>
          </div>
          <div class="trigger-area">
            <p>
              {{ !flexiblePool.opened ? $t('common.show') : $t('common.hide') }}
              <ChevronDown />
            </p>
          </div>
        </div>
        <div ref="poolDetails1000" :class="{'pool-details': true, 'active': flexiblePool.opened}">
          <div class="stake-container">
            <div class="desc-area">
              <div class="desc-row performance">
                <p class="desc">
                  {{ $t('stake.performance_fee') }}:
                </p>
                <p class="value">
                  0-{{ $priceFormat(flexiblePool.performanceFee, 0) + '%' }}
                </p>
              </div>
              <div class="desc-row performance">
                <p class="desc">
                  {{ $t('stake.unstaking_fee') }}:
                </p>
                <p class="value">
                  {{ $priceFormat(flexiblePool.withdrawFee, 1) + '%' }}
                </p>
              </div>
              <div class="desc-row performance">
                <p class="desc">
                  {{ $t('stake.unstaking_period') }}:
                </p>
                <p class="value">
                  {{ $priceFormat(flexiblePool.withdrawFeePeriod, 0) + $t('common.d') }}
                </p>
              </div>
              <div class="desc-row performance">
                <p class="desc">
                  {{ $t('stake.current_withdraw_fee') }}:
                </p>
                <p class="value highlighted">
                  {{ flexiblePool.info.unstakeFee + '%' }}
                </p>
              </div>
              <div class="desc-row status">
                <div class="status-bar auto">
                  <Refresh />
                  <span>
                    {{ $t('stake.auto') }}
                  </span>
                </div>
              </div>
            </div>
            <div class="numbers-area">
              <div class="input-area recent-profit">
                <div class="info">
                  <div class="h6">
                    {{ $t('stake.recent') + ' ' + $config.floatieName + ' ' + $t('stake.profit') }}
                  </div>
                  <div class="h3">
                    {{ $formatCryptoAmount(flexiblePool.earned, 6) }}
                    <span class="currency">
                      {{ $config.floatieSymbol }}
                    </span>
                  </div>
                  <div class="h5">
                    {{ convertToUsd(flexiblePool.earned) }}
                    <span class="currency">
                      USD
                    </span>
                  </div>
                </div>
                <div class="info">
                  <div class="h6">
                    {{ $t('stake.floatie_staked') }}
                  </div>
                  <div class="h3">
                    {{ $formatCryptoAmount(flexiblePool.info.lockedAmount) }}
                    <span class="currency">
                      {{ $config.floatieSymbol }}
                    </span>
                  </div>
                  <div class="h5">
                    {{ convertToUsd(flexiblePool.info.lockedAmount) + ' USD' }}
                  </div>
                </div>
              </div>
              <div class="buttons-area">
                <button v-if="flexiblePool.info.lockedAmount !== 0" type="button" :class="{'btn cyan': true, 'loading': stakeLoading, 'disabled': stakeLoading}" @click="prepareUnstaking(flexiblePool)">
                  {{ $t('stake.unstake') }}
                </button>
                <button type="button" :class="{'btn cyan': true, 'loading': stakeLoading, 'disabled': stakeLoading}" @click="prepareStaking(flexiblePool)">
                  {{ $t('stake.stake') }}
                </button>
              </div>
            </div>
          </div>
          <div class="help-area">
            <div class="help-list">
              <div class="item">
                <a class="bottom-link" :href="generateTokenInfoLink({address: $config.periodToken})" rel="nofollow noopener noreferrer" target="_blank">
                  <ChevronRight />
                  {{ $t('stake.token_info') }}
                </a>
              </div>
              <div class="item">
                <nuxt-link class="bottom-link" :to="localePath('/docs')">
                  <ChevronRight />
                  {{ $t('stake.view_tutorial') }}
                </nuxt-link>
              </div>
              <div class="item">
                <a class="bottom-link" :href="generateScanLink({address: $config.periodFlexibleVault})" rel="nofollow noopener noreferrer" target="_blank">
                  <ChevronRight />
                  {{ $t('stake.view_contract') }}
                </a>
              </div>
              <div class="item">
                <p class="bottom-link" @click="$addToMetamask(flexiblePool.token)">
                  <ChevronRight />
                  {{ $t('common.add_to_metamask') }}
                  <MetamaskIcon />
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="pool-item">
        <div class="pool-header">
          <div class="logo">
            <Skeleton width="42px" height="42px" circle />
          </div>
          <div class="header-area">
            <Skeleton width="150px" height="14px" style="margin-bottom: .25rem" />
            <Skeleton width="175px" height="18px" />
          </div>
          <div class="regular-item no-mobile-border">
            <div class="icon-area">
              <Skeleton width="21px" height="21px" />
            </div>
            <div class="value-area">
              <Skeleton width="45px" height="14px" style="margin-bottom: .25rem" />
              <Skeleton width="100px" height="18px" />
            </div>
          </div>
          <div class="regular-item">
            <div class="icon-area">
              <Skeleton width="21px" height="21px" />
            </div>
            <div class="value-area">
              <Skeleton width="45px" height="14px" style="margin-bottom: .25rem" />
              <Skeleton width="100px" height="18px" />
            </div>
          </div>
          <div class="regular-item">
            <div class="icon-area">
              <Skeleton width="21px" height="21px" />
            </div>
            <div class="value-area">
              <Skeleton width="45px" height="14px" style="margin-bottom: .25rem" />
              <Skeleton width="100px" height="18px" />
            </div>
          </div>
          <div class="regular-item">
            <div class="icon-area">
              <Skeleton width="21px" height="21px" />
            </div>
            <div class="value-area">
              <Skeleton width="45px" height="14px" style="margin-bottom: .25rem" />
              <Skeleton width="100px" height="18px" />
            </div>
          </div>
          <div class="trigger-area">
            <Skeleton width="50px" height="18x" />
          </div>
        </div>
      </div>
      <div v-for="(pool, index) in pools" :key="index" class="pool-item">
        <div :ref="'poolHeader' + index" :class="{'pool-header': true, 'active': pool.opened}" @click="toggleAccordion(index)">
          <div class="logo">
            <img :src="$getImage(pool.token.logoURI)" alt="$FLOATIE stake pool for earning auto flexible rewards" title="$FLOATIE stake pool">
            <div v-if="pool.mode === 'auto'" class="refresh-icon">
              <Refresh />
            </div>
          </div>
          <div class="header-area">
            <div class="h4">
              {{ $t('stake.stake') }} {{ pool.token.name }}
            </div>
            <p class="desc">
              {{ $t('stake.pool_desc') }}
            </p>
          </div>
          <div class="regular-item stacked-area no-mobile-border">
            <div class="icon-area">
              <Cube />
            </div>
            <div class="value-area">
              <p class="desc">
                {{ $t('stake.staked') }}
              </p>
              <p class="value">
                {{ $formatCryptoAmount(pool.staked) }} / {{ convertToUsd(pool.staked) }} USD
              </p>
            </div>
          </div>
          <div class="regular-item apy-area">
            <div class="icon-area">
              <Cubes />
            </div>
            <div class="value-area">
              <p class="desc">
                {{ $t('stake.total_staked') }}
              </p>
              <p class="value">
                {{ $formatCryptoAmount(pool.total) + ' ' + pool.token.symbol }}
              </p>
            </div>
          </div>
          <div class="regular-item apr-area">
            <div class="icon-area">
              <Cubes />
            </div>
            <div class="value-area">
              <p class="desc">
                {{ $t('stake.apr') }}
              </p>
              <p class="value">
                <span>
                  {{ pool.apr > 1000 ? $getBigNumber(pool.apr) : $priceFormat(pool.apr, 2) }}%
                </span>
                <span @click="openRoiCalculator(pool)">
                  <Calculator />
                </span>
              </p>
            </div>
          </div>
          <div class="regular-item total-area">
            <div class="icon-area">
              <Cubes />
            </div>
            <div class="value-area">
              <p class="desc">
                {{ $t('stake.ends_in') }}
              </p>
              <p class="value">
                {{ $priceFormat(pool.ends, 0) + ' ' + $t('stake.blocks') }}
              </p>
            </div>
          </div>
          <div class="trigger-area">
            <p>
              {{ !pool.opened ? $t('common.show') : $t('common.hide') }}
              <ChevronDown />
            </p>
          </div>
        </div>
        <div :ref="'poolDetails' + index" :class="{'pool-details': true, 'active': pool.opened}">
          <div class="stake-container">
            <div class="desc-area">
              <div class="desc-row total">
                <p class="desc">
                  {{ $t('stake.total_locked') }}:
                </p>
                <p class="value">
                  {{ $priceFormat(pool.apr, 2) + '%' }}
                </p>
              </div>
              <div v-if="pool.ends !== 0" class="desc-row avg-lock">
                <p class="desc">
                  {{ $t('stake.ends_in') }}:
                </p>
                <p class="value">
                  {{ $priceFormat(pool.ends, 0) + ' ' + $t('stake.blocks') }}
                </p>
              </div>
              <div v-else class="desc-row avg-lock">
                <p class="desc">
                  {{ $t('stake.ends_in') }}:
                </p>
                <p class="value">
                  ∞
                </p>
              </div>
              <div v-if="pool.index === 0" class="desc-row performance">
                <p class="desc">
                  {{ $t('stake.performance_fee') }}:
                </p>
                <p class="value">
                  ~{{ $priceFormat(pool.performance, 2) + '%' }}
                </p>
              </div>
              <div class="desc-row status">
                <div class="status-bar manual">
                  <Update />
                  <span>
                    {{ $t('stake.manual') }}
                  </span>
                </div>
              </div>
            </div>
            <div class="numbers-area">
              <div class="input-area recent-profit">
                <div class="info">
                  <div class="h6">
                    {{ $t('stake.recent') + ' ' + pool.token.name + ' ' + $t('stake.profit') }}
                  </div>
                  <div class="h3">
                    {{ $formatCryptoAmount(pool.pending) }}
                    <span class="currency">
                      {{ pool.token.symbol }}
                    </span>
                  </div>
                  <div class="h5">
                    {{ convertToUsd(pool.pending) }}
                    <span class="currency">
                      USD
                    </span>
                  </div>
                </div>
                <div class="action">
                  <button type="button" :class="{'btn cyan': true, 'disabled': pool.pending === 0 || stakeLoading}" @click="prepareUnstaking(pool)">
                    {{ $t('stake.unstake') }}
                  </button>
                </div>
              </div>
              <div class="input-area pool-actions">
                <div class="info">
                  <div class="h6">
                    {{ $t('stake.start_staking') }}
                  </div>
                  <input
                    v-model="pool.stakeAmount"
                    type="number"
                    step="0.00001"
                    lang="en"
                    min="0"
                    placeholder="0..."
                  >
                  <div class="h5">
                    {{ convertToUsd(pool.stakeAmount) }}
                    <span class="currency">
                      USD
                    </span>
                  </div>
                </div>
                <div class="action">
                  <button type="button" :class="{'btn cyan': true, 'loading': pool.loadingStake || stakeLoading, 'disabled': pool.stakeAmount === null || pool.stakeAmount === 0 || stakeLoading}" @click="prepareStaking(pool)">
                    {{ $t('stake.stake') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="help-area">
            <div class="help-list">
              <div class="item">
                <a class="bottom-link" :href="generateTokenInfoLink(pool.token)" rel="nofollow noopener noreferrer" target="_blank">
                  <ChevronRight />
                  {{ $t('stake.token_info') }}
                </a>
              </div>
              <div class="item">
                <nuxt-link class="bottom-link" :to="localePath('/docs')">
                  <ChevronRight />
                  {{ $t('stake.view_tutorial') }}
                </nuxt-link>
              </div>
              <div class="item">
                <a class="bottom-link" :href="generateScanLink(pool.token)" rel="nofollow noopener noreferrer" target="_blank">
                  <ChevronRight />
                  {{ $t('stake.view_contract') }}
                </a>
              </div>
              <div class="item">
                <p class="bottom-link" @click="$addToMetamask(pool.token)">
                  <ChevronRight />
                  {{ $t('common.add_to_metamask') }}
                  <MetamaskIcon />
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="pools-area">
      <div class="loading-area">
        <div v-for="index in 2" :key="index" class="pool-item">
          <div class="pool-header">
            <div class="logo">
              <Skeleton width="42px" height="42px" circle />
            </div>
            <div class="header-area">
              <Skeleton width="150px" height="14px" style="margin-bottom: .25rem" />
              <Skeleton width="175px" height="18px" />
            </div>
            <div class="regular-item no-mobile-border">
              <div class="icon-area">
                <Skeleton width="21px" height="21px" />
              </div>
              <div class="value-area">
                <Skeleton width="45px" height="14px" style="margin-bottom: .25rem" />
                <Skeleton width="100px" height="18px" />
              </div>
            </div>
            <div class="regular-item">
              <div class="icon-area">
                <Skeleton width="21px" height="21px" />
              </div>
              <div class="value-area">
                <Skeleton width="45px" height="14px" style="margin-bottom: .25rem" />
                <Skeleton width="100px" height="18px" />
              </div>
            </div>
            <div class="regular-item">
              <div class="icon-area">
                <Skeleton width="21px" height="21px" />
              </div>
              <div class="value-area">
                <Skeleton width="45px" height="14px" style="margin-bottom: .25rem" />
                <Skeleton width="100px" height="18px" />
              </div>
            </div>
            <div class="regular-item">
              <div class="icon-area">
                <Skeleton width="21px" height="21px" />
              </div>
              <div class="value-area">
                <Skeleton width="45px" height="14px" style="margin-bottom: .25rem" />
                <Skeleton width="100px" height="18px" />
              </div>
            </div>
            <div class="trigger-area">
              <Skeleton width="50px" height="18x" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { Skeleton } from 'vue-loading-skeleton'
import {
  hydrateWhenIdle,
  hydrateWhenVisible
} from 'vue-lazy-hydration'

import Calculator from '~/components/svg/new/Calculator'
import ChevronDown from '~/components/svg/new/ChevronDown'
import StakeAsset from '~/components/app/interface/StakeAsset'

export default {
  name: 'StakeComponent',
  components: {
    Calculator,
    ChevronDown,
    ChevronRight: hydrateWhenVisible(() => import('~/components/svg/new/ChevronRight')),
    CloudCyanStakeLeft: hydrateWhenIdle(() => import('~/components/svg/new/clouds/CloudCyanStakeLeft')),
    CloudCyanStakeRight: hydrateWhenIdle(() => import('~/components/svg/new/clouds/CloudCyanStakeRight')),
    Cube: hydrateWhenVisible(() => import('~/components/svg/new/Cube')),
    Cubes: hydrateWhenVisible(() => import('~/components/svg/new/Cubes')),
    MetamaskIcon: hydrateWhenVisible(() => import('~/components/svg/MetamaskIcon')),
    Refresh: hydrateWhenVisible(() => import('~/components/svg/new/Refresh')),
    RoiCalculator: hydrateWhenIdle(() => import('~/components/modals/RoiCalculator')),
    Search: hydrateWhenIdle(() => import('~/components/svg/new/Search')),
    Skeleton,
    Select: hydrateWhenIdle(() => import('~/components/app/interface/Select')),
    StakeAsset,
    Switcher: hydrateWhenIdle(() => import('~/components/app/interface/Switcher')),
    Update: hydrateWhenVisible(() => import('~/components/svg/new/Update')),
    WarnCircleIcon: hydrateWhenVisible(() => import('~/components/svg/WarnCircleIcon')),
    WindowModal: hydrateWhenIdle(() => import('~/components/modals/WindowModal'))
  },
  layout: 'new',
  data() {
    return {
      actionPicked: 'stake',
      currentPool: null,
      modalTitle: this.$t('stake.you_are_staking'),
      filter: {
        page: 1,
        step: 10,
        stakedOnly: false,
        status: 'live',
        sort: 'hot',
        search: ''
      },
      currentStake: null,
      currentStakeDepend: {
        apr: 0,
        deposited: 0,
        type: 'flexible',
        balance: 0,
        performanceFee: 0
      },
      transaction: {
        gasFee: 700000,
        tx: '',
        loading: false,
        pending: false
      },
      filterOptions: [
        {
          name: this.$t('stake.hot'),
          value: 'hot'
        },
        {
          name: this.$t('stake.apr'),
          value: 'apr'
        },
        {
          name: this.$t('stake.earned'),
          value: 'earned'
        },
        {
          name: this.$t('stake.total_staked'),
          value: 'total_staked'
        },
        {
          name: this.$t('stake.latest'),
          value: 'latest'
        }
      ],
      stakeLoading: false,
      poolsLoading: false,
      pools: [],
      lockedPool: null,
      lockedPoolLoading: true,
      flexiblePool: null,
      flexiblePoolLoading: true
    }
  },
  async fetch({ store, error, route }) {
    try {
      const pageName = route.name.split('___')
      const seoData = store.getters['seos/seosByUri'](pageName[0])
      if (seoData === null) {
        await store.dispatch('seos/fetchSeos', {
          uri: route.name
        })
      }
    } catch (err) {
      error(err)
    }
  },
  head({ $config: { appUrl } }) {
    return {
      title: this.meta ? this.meta.title : this.$t('common.launching'),
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.meta ? this.meta.description : ''
        },
        {
          hid: 'og-description',
          name: 'og:description',
          content: this.meta ? this.meta.description : ''
        },
        {
          hid: 'og-title',
          name: 'og:title',
          content: this.meta
            ? this.meta.title
            : this.$t('common.launching')
        },
        {
          hid: 'og-image',
          name: 'og:image',
          content: this.meta
            ? this.meta.image
            : appUrl + 'seo/default.jpg'
        },
        {
          hid: 'keywords',
          name: 'keywords',
          content: this.meta ? this.meta.keyword : ''
        }
      ]
    }
  },
  computed: {
    ...mapGetters('staking', [
      'poolsTotal'
    ]),
    ...mapGetters('seos', [
      'seosByUri'
    ]),
    ...mapGetters(['floatieToUsd']),
    meta() {
      const pageName = this.$nuxt.$route.name.split('___')
      return this.seosByUri(pageName[0])
    }
  },
  mounted() {
    this.loadlockedPool()
    this.loadFlexiblePool()
    this.loadPools()
    if (window.ethereum !== undefined) {
      window.ethereum.on('accountsChanged', () => {
        this.loadlockedPool()
        this.loadFlexiblePool()
        this.loadPools()
      })
    }
  },
  methods: {
    ...mapActions('staking', [
      'fetchPools'
    ]),
    ...mapActions('modal', [
      'changeModalGlobalState',
      'changeModalRoiState'
    ]),
    generateTokenInfoLink(token) {
      return this.$config.appUrl + 'analytics/tokens/' + token.address
    },
    generateScanLink(token) {
      return this.$config.chainExplorer + '/address/' + token.address
    },
    changeStakedOnly(newState) {
      this.filter.stakedOnly = newState === true
    },
    setStatus(newStatus) {
      this.filter.status = newStatus
    },
    toggleAccordion(index) {
      const poolItem = index < 999 ? this.$refs['poolDetails' + index][0] : this.$refs['poolDetails' + index]
      if (poolItem === undefined) {
        return
      }
      const poolIndex = this.pools.findIndex(item => item.index === index)
      if (index < 999) {
        this.pools[poolIndex].opened = !this.pools[poolIndex].opened
      } else if (index === 999) {
        this.lockedPool.opened = !this.lockedPool.opened
      } else {
        this.flexiblePool.opened = !this.flexiblePool.opened
      }
      const opened = index < 999 ? this.pools[poolIndex].opened : (index === 999 ? this.lockedPool.opened : this.flexiblePool.opened)
      if (opened) {
        const height = poolItem.scrollHeight + 'px'
        poolItem.style.maxHeight = height
      } else {
        poolItem.style.maxHeight = null
      }
    },
    convertToUsd(amount) {
      return this.$priceFormat(parseFloat(amount * this.floatieToUsd), 2)
    },
    async loadPools() {
      const chainId = await window.eth.eth.net.getId()
      if (!this.$isChainSupported(chainId)) {
        this.poolsLoading = false
        return
      }
      this.poolsLoading = true
      const chainOk = await this.$replaceChain()
      if (!chainOk) {
        this.poolsLoading = false
        return
      }
      await this.fetchPools(this.filter)
      this.pools = this.$store.getters['staking/pools']
      this.poolsLoading = false
    },
    async stakeLocked(extendLock, stakeInputAmount = 0, weeksExtend = 0) {
      const depositSummary = this.$t('common.stake_action') + ' ' + this.$formatCryptoAmount(stakeInputAmount) + ' ' +
        this.$config.floatieSymbol + ' ' + this.$t('common.to_the_pool')

      this.lockedPool.stakeAmount = parseFloat(stakeInputAmount)
      const userAddress = await this.$getUserAddress()
      const FloatieVaultFile = await import('~/assets/abis/FloatieVault.json')
      const FloatieVault = FloatieVaultFile.default
      const vaultContract = new window.eth.eth.Contract(FloatieVault, this.$config.periodVault)
      const stakeAmount = window.eth.utils.toBN(
        window.eth.utils.toWei(
          Number(parseFloat(this.lockedPool.stakeAmount)).toPrecision(10)
        )
      )

      if (this.lockedPool.stakeAmount > 0) {
        const ERC20File = await import('~/assets/abis/ERC20.json')
        const ERC20 = ERC20File.default
        const tokenContract = new window.eth.eth.Contract(ERC20, this.$config.periodToken)

        const lpTokenBalance = window.eth.utils.fromWei(await tokenContract.methods.balanceOf(userAddress).call({ from: userAddress }))
        if (parseFloat(lpTokenBalance) < this.lockedPool.stakeAmount) {
          this.showError('error', this.$t('stake.low_balance'))
          this.stakeLoading = false
          return
        }
        const lpTokenAllowance = window.eth.utils.toBN(await tokenContract.methods.allowance(userAddress, this.$config.periodVault).call({ from: userAddress }))
        if (stakeAmount.gte(lpTokenAllowance)) {
          try {
            const gasPrice = await this.$calculateGasCost()
            const gasFeeEstimated = await tokenContract.methods.approve(this.$config.periodVault, window.eth.utils.toWei(Number(2 ** 64 - 1).toString(), 'ether'))
              .estimateGas({ from: userAddress, gas: 5000000 })
            const approved = await tokenContract.methods.approve(this.$config.periodVault, window.eth.utils.toWei(Number(2 ** 64 - 1).toString(), 'ether'))
              .send({ from: userAddress, gas: Math.round(gasFeeEstimated * 1.15).toString(), gasPrice })

            console.log(approved)
          } catch (err) {
            if (err.code === 4001) {
              this.showError('error', this.$t('common.deined_operation'))
            } else {
              const errorMessage = err.message !== undefined && err.message.length > 0 ? err.message : ''
              this.showError('error', errorMessage)
            }
            this.stakeLoading = false
            console.log(err)
          }
        }
      }

      try {
        const startDate = parseInt(this.lockedPool.info.lockStartTime, 10) !== 0 ? new Date(this.lockedPool.info.lockStartTime * 1000) : new Date()
        const endDate = parseInt(this.lockedPool.info.lockEndTime, 10) !== 0 ? new Date(this.lockedPool.info.lockEndTime * 1000) : new Date()
        const diffTime = Math.abs(endDate - startDate)
        const weeksInSec = weeksExtend * 7 * 24 * 60 * 60
        const timeToDepositSec = extendLock ? diffTime / 1000 : weeksInSec
        const gasPrice = await this.$calculateGasCost()
        const depositGasFee = await vaultContract.methods.deposit(stakeAmount, timeToDepositSec)
          .estimateGas({ from: userAddress, gas: 5000000 })
        const deposited = await vaultContract.methods.deposit(stakeAmount, timeToDepositSec)
          .send({ from: userAddress, gas: Math.round(depositGasFee * 1.15).toString(), gasPrice })
        console.log(deposited)
        this.$saveTransaction({
          tid0: this.$config.periodToken,
          t0_amount: this.lockedPool.stakeAmount,
          hash: deposited.transactionHash,
          type: 'stake'
        },
        depositSummary
        )
        this.transaction.tx = deposited.transactionHash
        if (this.$refs.stakeAsset !== undefined) {
          this.$refs.stakeAsset.initSuccessScreen()
        }
        this.loadlockedPool()
      } catch (err) {
        if (err.code === 4001) {
          this.showError('error', this.$t('common.deined_operation'))
        } else {
          const errorMessage = err.message !== undefined && err.message.length > 0 ? err.message : ''
          this.showError('error', errorMessage)
          this.stakeLoading = false
        }
        console.log(err)
      }
      this.lockedPool.stakeAmount = null
    },
    async unstakeLocked(stakeAmount) {
      const withdrawAmount = parseFloat(stakeAmount)
      const depositSummary = this.$t('common.unstake_action') + ' ' + this.$formatCryptoAmount(withdrawAmount) + ' ' +
        this.$config.floatieSymbol + ' ' + this.$t('common.from_the_pool')

      if (withdrawAmount === undefined || withdrawAmount === 0) {
        this.showError('error', this.$t('stake.amount_unstake_incorrect'))
        this.stakeLoading = false
        return
      }
      const userAddress = await this.$getUserAddress()
      const FloatieVaultFile = await import('~/assets/abis/FloatieVault.json')
      const FloatieVault = FloatieVaultFile.default
      const vaultContract = new window.eth.eth.Contract(FloatieVault, this.$config.periodVault)
      const amountWithdrawn = window.eth.utils.toBN(
        window.eth.utils.toWei(
          Number(parseFloat(withdrawAmount)).toPrecision(10)
        )
      )

      try {
        const gasPrice = await this.$calculateGasCost()
        const depositGasFee = await vaultContract.methods.withdraw(amountWithdrawn)
          .estimateGas({ from: userAddress, gas: 5000000 })
        const withdrown = await vaultContract.methods.withdraw(amountWithdrawn)
          .send({ from: userAddress, gas: Math.round(depositGasFee * 1.15).toString(), gasPrice })
        console.log(withdrown)
        this.$saveTransaction({
          tid0: this.$config.periodToken,
          t0_amount: withdrawAmount,
          hash: withdrown.transactionHash,
          type: 'unstake'
        },
        depositSummary
        )
        this.transaction.tx = withdrown.transactionHash
        if (this.$refs.stakeAsset !== undefined) {
          this.$refs.stakeAsset.initSuccessScreen()
        }
        this.loadlockedPool()
      } catch (err) {
        if (err.code === 4001) {
          this.showError('error', this.$t('common.deined_operation'))
        } else {
          const errorMessage = err.message !== undefined && err.message.length > 0 ? err.message : ''
          this.showError('error', errorMessage)
          this.stakeLoading = false
        }
        console.log(err)
      }
    },
    async stakeFlexible(stakeInputAmount = 0) {
      const depositSummary = this.$t('common.stake_action') + ' ' + this.$formatCryptoAmount(stakeInputAmount) + ' ' +
        this.$config.floatieSymbol + ' ' + this.$t('common.to_the_pool')

      this.flexiblePool.stakeAmount = parseFloat(stakeInputAmount)
      const userAddress = await this.$getUserAddress()
      const FloatieVaultFile = await import('~/assets/abis/FloatieFlexibleVault.json')
      const FloatieVault = FloatieVaultFile.default
      const vaultContract = new window.eth.eth.Contract(FloatieVault, this.$config.periodFlexibleVault)
      const stakeAmount = window.eth.utils.toBN(
        window.eth.utils.toWei(
          Number(parseFloat(this.flexiblePool.stakeAmount)).toPrecision(10)
        )
      )

      if (this.flexiblePool.stakeAmount > 0) {
        const ERC20File = await import('~/assets/abis/ERC20.json')
        const ERC20 = ERC20File.default
        const tokenContract = new window.eth.eth.Contract(ERC20, this.$config.periodToken)

        const lpTokenBalance = window.eth.utils.fromWei(await tokenContract.methods.balanceOf(userAddress).call({ from: userAddress }))
        if (parseFloat(lpTokenBalance) < this.flexiblePool.stakeAmount) {
          this.showError('error', this.$t('stake.low_balance'))
          this.stakeLoading = false
          return
        }
        const lpTokenAllowance = window.eth.utils.toBN(await tokenContract.methods.allowance(userAddress, this.$config.periodFlexibleVault).call({ from: userAddress }))
        if (stakeAmount.gte(lpTokenAllowance)) {
          try {
            const gasPrice = await this.$calculateGasCost()
            const gasFeeEstimated = await tokenContract.methods.approve(this.$config.periodFlexibleVault, window.eth.utils.toWei(Number(2 ** 64 - 1).toString(), 'ether'))
              .estimateGas({ from: userAddress, gas: 5000000 })
            const approved = await tokenContract.methods.approve(this.$config.periodFlexibleVault, window.eth.utils.toWei(Number(2 ** 64 - 1).toString(), 'ether'))
              .send({ from: userAddress, gas: Math.round(gasFeeEstimated * 1.15).toString(), gasPrice })

            console.log(approved)
          } catch (err) {
            if (err.code === 4001) {
              this.showError('error', this.$t('common.deined_operation'))
            } else {
              const errorMessage = err.message !== undefined && err.message.length > 0 ? err.message : ''
              this.showError('error', errorMessage)
            }
            this.stakeLoading = false
            console.log(err)
          }
        }
      }

      try {
        const gasPrice = await this.$calculateGasCost()
        const depositGasFee = await vaultContract.methods.deposit(stakeAmount)
          .estimateGas({ from: userAddress, gas: 5000000 })
        const deposited = await vaultContract.methods.deposit(stakeAmount)
          .send({ from: userAddress, gas: Math.round(depositGasFee * 1.15).toString(), gasPrice })
        console.log(deposited)
        this.$saveTransaction({
          tid0: this.$config.periodToken,
          t0_amount: this.flexiblePool.stakeAmount,
          hash: deposited.transactionHash,
          type: 'stake'
        },
        depositSummary
        )
        this.transaction.tx = deposited.transactionHash
        if (this.$refs.stakeAsset !== undefined) {
          this.$refs.stakeAsset.initSuccessScreen()
        }
        this.loadFlexiblePool()
      } catch (err) {
        if (err.code === 4001) {
          this.showError('error', this.$t('common.deined_operation'))
        } else {
          const errorMessage = err.message !== undefined && err.message.length > 0 ? err.message : ''
          this.showError('error', errorMessage)
          this.stakeLoading = false
        }
        console.log(err)
      }
      this.flexiblePool.stakeAmount = null
    },
    async unstakeFlexible(stakeAmount) {
      const withdrawAmount = parseFloat(stakeAmount)
      const depositSummary = this.$t('common.unstake_action') + ' ' + this.$formatCryptoAmount(withdrawAmount) + ' ' +
        this.$config.floatieSymbol + ' ' + this.$t('common.from_the_pool')

      if (withdrawAmount === undefined || withdrawAmount === 0) {
        this.showError('error', this.$t('stake.amount_unstake_incorrect'))
        this.stakeLoading = false
        return
      }
      const userAddress = await this.$getUserAddress()
      const FloatieVaultFile = await import('~/assets/abis/FloatieFlexibleVault.json')
      const FloatieVault = FloatieVaultFile.default
      const vaultContract = new window.eth.eth.Contract(FloatieVault, this.$config.periodFlexibleVault)
      const amountWithdrawn = window.eth.utils.toBN(
        window.eth.utils.toWei(
          Number(parseFloat(withdrawAmount)).toPrecision(10)
        )
      )

      try {
        const gasPrice = await this.$calculateGasCost()
        const depositGasFee = await vaultContract.methods.withdraw(amountWithdrawn)
          .estimateGas({ from: userAddress, gas: 5000000 })
        const withdrown = await vaultContract.methods.withdraw(amountWithdrawn)
          .send({ from: userAddress, gas: Math.round(depositGasFee * 1.15).toString(), gasPrice })
        console.log(withdrown)
        this.$saveTransaction({
          tid0: this.$config.periodToken,
          t0_amount: withdrawAmount,
          hash: withdrown.transactionHash,
          type: 'unstake'
        },
        depositSummary
        )
        this.transaction.tx = withdrown.transactionHash
        if (this.$refs.stakeAsset !== undefined) {
          this.$refs.stakeAsset.initSuccessScreen()
        }
        this.loadFlexiblePool()
      } catch (err) {
        if (err.code === 4001) {
          this.showError('error', this.$t('common.deined_operation'))
        } else {
          const errorMessage = err.message !== undefined && err.message.length > 0 ? err.message : ''
          this.showError('error', errorMessage)
          this.stakeLoading = false
        }
        console.log(err)
      }
    },
    async extendLocked(weeksDuration) {
      if (weeksDuration === null || weeksDuration > 51) {
        this.showError('error', this.$t('stake.exceeded_week_duration'))
      }

      const userAddress = await this.$getUserAddress()
      const FloatieVaultFile = await import('~/assets/abis/FloatieVault.json')
      const FloatieVault = FloatieVaultFile.default
      const vaultContract = new window.eth.eth.Contract(FloatieVault, this.$config.periodVault)
      try {
        const timeToDepositSec = weeksDuration * 7 * 24 * 60 * 60
        const gasPrice = await this.$calculateGasCost()
        const depositGasFee = await vaultContract.methods.deposit(0, timeToDepositSec)
          .estimateGas({ from: userAddress, gas: 5000000 })
        const deposited = await vaultContract.methods.deposit(0, timeToDepositSec)
          .send({ from: userAddress, gas: Math.round(depositGasFee * 1.15).toString(), gasPrice })
        console.log(deposited)

        const depositSummary = this.$t('commstakeon.extending_for') + ' ' + weeksDuration + ' ' +
          this.$num2str([this.$t('common.week'), this.$t('common.weeks'), this.$t('common.many_weeks')], weeksDuration)
        this.$saveTransaction({
          tid0: this.$config.periodToken,
          t0_amount: 0,
          hash: deposited.transactionHash,
          type: 'extend'
        },
        depositSummary
        )
        this.transaction.tx = deposited.transactionHash
        if (this.$refs.stakeAsset !== undefined) {
          this.$refs.stakeAsset.initSuccessScreen()
        }
        this.loadlockedPool()
      } catch (err) {
        if (err.code === 4001) {
          this.showError('error', this.$t('common.deined_operation'))
        } else {
          const errorMessage = err.message !== undefined && err.message.length > 0 ? err.message : ''
          this.showError('error', errorMessage)
          this.stakeLoading = false
        }
        console.log(err)
      }
    },
    async stakePool(pool, stakeInputAmount, extendLock, weeksExtend) {
      this.transaction.loading = true
      this.transaction.pending = true
      const chainOk = await this.$replaceChain()
      if (!chainOk) {
        this.transaction.loading = false
        this.transaction.pending = false
        return
      }
      if (stakeInputAmount === null || parseFloat(stakeInputAmount) === 0) {
        this.showError('error', this.$t('stake.amount_stake_incorrect'))
        this.stakeLoading = false
        return
      }
      const depositAmount = parseFloat(stakeInputAmount)
      if (pool.type !== undefined) {
        switch (pool.type.toLowerCase()) {
          case 'locked':
            await this.stakeLocked(extendLock, stakeInputAmount, weeksExtend)
            break
          case 'flexible':
            await this.stakeFlexible(stakeInputAmount)
            break
        }
        this.transaction.loading = false
        this.transaction.pending = false
      } else {
        const userAddress = await this.$getUserAddress()
        const ERC20File = await import('~/assets/abis/ERC20.json')
        const ERC20 = ERC20File.default
        const MasterChefFile = await import('~/assets/abis/MasterChef.json')
        const MasterChef = MasterChefFile.default
        const masterContract = new window.eth.eth.Contract(MasterChef, this.$config.periodMaster)
        const tokenContract = new window.eth.eth.Contract(ERC20, pool.token.address)

        const lpTokenBalance = window.eth.utils.fromWei(await tokenContract.methods.balanceOf(userAddress).call({ from: userAddress }))
        if (parseFloat(lpTokenBalance) <= depositAmount) {
          this.showError('error', this.$t('stake.low_balance'))
          this.stakeLoading = false
          return
        }
        const lpTokenAllowance = window.eth.utils.toBN(await tokenContract.methods.allowance(userAddress, this.$config.periodMaster).call({ from: userAddress }))
        const stakeAmount = window.eth.utils.toBN(
          window.eth.utils.toWei(
            Number(depositAmount).toPrecision(10)
          )
        )
        if (stakeAmount.gte(lpTokenAllowance)) {
          try {
            const gasPrice = await this.$calculateGasCost()
            const gasFeeEstimated = await tokenContract.methods.approve(this.$config.periodMaster, window.eth.utils.toWei(Number(2 ** 64 - 1).toString(), 'ether'))
              .estimateGas({ from: userAddress, gas: 5000000 })
            const approved = await tokenContract.methods.approve(this.$config.periodMaster, window.eth.utils.toWei(Number(2 ** 64 - 1).toString(), 'ether'))
              .send({ from: userAddress, gas: Math.round(gasFeeEstimated * 1.15).toString(), gasPrice })

            console.log(approved)
          } catch (err) {
            if (err.code === 4001) {
              this.showError('denied')
            } else {
              const errorMessage = err.message !== undefined && err.message.length > 0 ? err.message : ''
              if (this.$refs.stakeAsset !== undefined) {
                this.showError('error', errorMessage)
              }
            }
            this.stakeLoading = false
            console.log(err)
          }
        }

        try {
          const gasPrice = await this.$calculateGasCost()
          const depositGasFee = await masterContract.methods.enterStaking(stakeAmount)
            .estimateGas({ from: userAddress, gas: 5000000 })
          const deposited = await masterContract.methods.enterStaking(stakeAmount)
            .send({ from: userAddress, gas: Math.round(depositGasFee * 1.15).toString(), gasPrice })
          console.log(deposited)
          const depositSummary = this.$t('common.stake_action') + ' ' + this.$formatCryptoAmount(depositAmount) + ' ' +
          pool.token.symbol + ' ' + this.$t('common.to_the_pool')
          this.$saveTransaction({
            tid: pool.token.address,
            t0_amount: depositAmount,
            hash: deposited.transactionHash,
            type: 'stake'
          },
          depositSummary
          )
          this.transaction.tx = deposited.transactionHash
          pool.staked += depositAmount
          if (this.$refs.stakeAsset !== undefined) {
            this.$refs.stakeAsset.initSuccessScreen()
          }
        } catch (err) {
          if (err.code === 4001) {
            this.showError('denied')
          } else {
            const errorMessage = err.message !== undefined && err.message.length > 0 ? err.message : ''
            if (this.$refs.stakeAsset !== undefined) {
              this.showError('error', errorMessage)
            }
          }
          this.stakeLoading = false
          console.log(err)
        }
      }
      this.transaction.loading = false
      this.transaction.pending = false
      this.stakeLoading = false
    },
    async unstakePool(pool, unstakeAmount = 0) {
      this.transaction.loading = true
      this.transaction.pending = true
      const chainOk = await this.$replaceChain()
      if (!chainOk) {
        this.transaction.loading = false
        this.transaction.pending = false
        return
      }
      if (unstakeAmount === null || parseFloat(unstakeAmount) === 0) {
        this.showError('error', this.$t('stake.amount_stake_incorrect'))
        this.stakeLoading = false
        return
      }
      this.stakeLoading = true
      if (pool.type !== undefined) {
        switch (pool.type.toLowerCase()) {
          case 'locked':
            await this.unstakeLocked(unstakeAmount)
            break
          case 'flexible':
            await this.unstakeFlexible(unstakeAmount)
            break
        }
        this.transaction.loading = false
        this.transaction.pending = false
      } else {
        this.transaction.loading = true
        this.transaction.pending = true
        pool.loadingHarvest = true
        const chainOk = await this.$replaceChain()
        if (!chainOk) {
          this.transaction.loading = false
          this.transaction.pending = false
          pool.loadingHarvest = false
          return
        }
        const userAddress = await this.$getUserAddress()
        const MasterChefFile = await import('~/assets/abis/MasterChef.json')
        const MasterChef = MasterChefFile.default
        const masterContract = new window.eth.eth.Contract(MasterChef, this.$config.periodMaster)

        const stakedBalance = await masterContract.methods.userInfo(pool.index, userAddress).call({ from: userAddress })
        const stakedAmount = parseFloat(unstakeAmount)
        if (stakedAmount === 0) {
          this.showError('error', this.$t('stake.nothing_staked'))
          pool.loadingHarvest = false
          return
        }
        const gasPrice = await this.$calculateGasCost()
        try {
          const withdrawGasFee = await masterContract.methods.leaveStaking(stakedBalance.amount)
            .estimateGas({ from: userAddress, gas: 5000000 })
          const withdrawed = await masterContract.methods.leaveStaking(stakedBalance.amount)
            .send({ from: userAddress, gas: Math.round(withdrawGasFee * 1.15).toString(), gasPrice })

          console.log(withdrawed)
          const depositSummary = this.$t('common.unstake_action') + ' ' + this.$formatCryptoAmount(stakedAmount) + ' ' +
          pool.token.symbol + ' ' + this.$t('common.from_the_pool')
          this.$saveTransaction({
            tid0: pool.token.address,
            t0_amount: pool.stakeAmount,
            hash: withdrawed.transactionHash,
            type: 'unstake'
          },
          depositSummary
          )
          this.transaction.tx = withdrawed.transactionHash
          console.log(this.transaction.tx)

          pool.staked -= stakedAmount
          if (this.$refs.stakeAsset !== undefined) {
            this.$refs.stakeAsset.initSuccessScreen()
          }
          this.transaction.loading = false
          this.transaction.pending = false
          pool.loadingHarvest = false
        } catch (err) {
          if (err.code === 4001) {
            this.showError('denied')
          } else {
            const errorMessage = err.message !== undefined && err.message.length > 0 ? err.message : ''
            if (this.$refs.stakeAsset !== undefined) {
              this.showError('error', errorMessage)
            }
          }
          console.log(err)
        }
      }
    },
    async convertStaking(pool) {
      this.transaction.loading = true
      this.transaction.pending = true
      const chainOk = await this.$replaceChain()
      if (!chainOk) {
        this.transaction.loading = false
        this.transaction.pending = false
        return
      }
      this.stakeLoading = true
      const userAddress = await this.$getUserAddress()
      const FloatieVaultFile = await import('~/assets/abis/FloatieVault.json')
      const FloatieVault = FloatieVaultFile.default
      const masterContract = new window.eth.eth.Contract(FloatieVault, this.$config.periodVault)

      const gasPrice = await this.$calculateGasCost()
      try {
        const unlockGasFee = await masterContract.methods.unlock(userAddress)
          .estimateGas({ from: userAddress, gas: 5000000 })
        const unlocked = await masterContract.methods.unlock(userAddress)
          .send({ from: userAddress, gas: Math.round(unlockGasFee * 1.15).toString(), gasPrice })

        console.log(unlocked)
        const depositSummary = this.$t('common.unlock_action') + ' ' + this.$formatCryptoAmount(pool.info.lockedAmount) + ' ' +
          pool.token.symbol
        this.$saveTransaction({
          tid0: pool.token.address,
          t0_amount: pool.info.lockedAmount,
          hash: unlocked.transactionHash,
          type: 'unstake'
        },
        depositSummary
        )
        this.transaction.tx = unlocked.transactionHash
        console.log(this.transaction.tx)

        pool.totalLocked -= pool.info.lockedAmount
        if (this.$refs.stakeAsset !== undefined) {
          this.$refs.stakeAsset.initSuccessScreen()
        }
        this.transaction.loading = false
        this.transaction.pending = false
        this.stakeLoading = false
      } catch (err) {
        if (err.code === 4001) {
          this.showError('denied')
        } else {
          const errorMessage = err.message !== undefined && err.message.length > 0 ? err.message : ''
          if (this.$refs.stakeAsset !== undefined) {
            this.showError('error', errorMessage)
          }
        }
        console.log(err)
      }
    },
    changeModalTitle(titleType) {
      switch (titleType) {
        case 'loading':
          this.modalTitle = this.$t('swap.waiting_for_confirmation')
          break
        case 'success':
          this.modalTitle = this.$t('stake.success')
          break
        case 'error':
          this.modalTitle = this.$t('swap.error_header')
          break
        default:
          this.modalTitle = this.actionPicked === 'stake' ? this.$t('stake.you_are_staking') : (this.actionPicked === 'unstake' ? this.$t('stake.you_are_unstaking') : this.$t('stake.you_are_extending'))
      }
    },
    async prepareStaking(pool) {
      const userAddress = await this.$getUserAddress()
      const ERC20File = await import('~/assets/abis/ERC20.json')
      const ERC20 = ERC20File.default
      const tokenContract = new window.eth.eth.Contract(ERC20, pool.token.address)
      pool.balance = parseFloat(window.eth.utils.fromWei(await tokenContract.methods.balanceOf(userAddress).call({ from: userAddress })))
      pool.balance = pool.balance < 1e-12 ? 0 : pool.balance
      this.currentPool = pool
      this.actionPicked = 'stake'
      switch (this.currentPool.type) {
        case 'locked':
          this.currentPool.ctaType = pool.info.locked === true && pool.info.lockedAmount > 0 ? 'add_floatie' : 'stake'
          break
        case 'flexible':
          this.currentPool.ctaType = 'stake'
          break
      }
      this.changeModalGlobalState({
        state: true
      })
    },
    prepareUnstaking(pool) {
      this.currentPool = pool
      this.actionPicked = 'unstake'
      this.changeModalGlobalState({
        state: true
      })
    },
    prepareExtend(pool) {
      this.currentPool = pool
      this.actionPicked = 'extend'
      this.changeModalGlobalState({
        state: true
      })
    },
    async openRoiCalculator(stake) {
      const userAddress = await this.$getUserAddress()
      const ERC20File = await import('~/assets/abis/ERC20.json')
      const ERC20 = ERC20File.default
      const tokenContract = new window.eth.eth.Contract(ERC20, stake.token.address)
      const balance = parseFloat(window.eth.utils.fromWei(await tokenContract.methods.balanceOf(userAddress).call({ from: userAddress })))
      this.currentStake = stake
      this.currentStakeDepend.performanceFee = stake.performanceFee !== undefined ? stake.performanceFee : 2
      this.currentStakeDepend.apr = stake.apr !== undefined ? stake.apr : 0
      this.currentStakeDepend.type = stake.type !== undefined ? stake.type : 'flexible'
      this.currentStakeDepend.balance = balance < 1e-12 ? 0 : balance
      if (stake.type !== undefined) {
        this.currentStakeDepend.deposited = stake.type === 'flexible' ? stake.info.lockedAmount : stake.info.floatieAtLastUserAction
      } else {
        this.currentStakeDepend.deposited = 0
      }
      console.log(this.currentStakeDepend)
      this.changeModalRoiState({
        state: true
      })
    },
    closeModal() {
      this.changeModalGlobalState({
        state: false
      })
    },
    showError(type, message = '') {
      this.transaction.loading = false
      this.transaction.pending = false
      this.changeModalGlobalState({
        state: true
      })
      if (this.$refs.stakeAsset !== undefined) {
        this.$refs.stakeAsset.rejectTransaction({
          type,
          message
        })
      } else {
        console.error(type, message)
      }
    },
    async loadlockedPool() {
      const chainId = await window.eth.eth.net.getId()
      if (!this.$isChainSupported(chainId)) {
        this.lockedPoolLoading = false
        return
      }
      this.lockedPoolLoading = true
      const answer = {
        type: 'locked',
        opened: false,
        performanceFee: 0, // performanceFee
        totalLocked: 0, // totalLockedAmount
        totalStaked: 0, // available
        withdrawFee: 0, // withdrawFee
        withdrawFeePeriod: 0, // withdrawFeePeriod
        info: {
          shares: 0,
          lastDepostTime: 0,
          lastUserActionTime: 0,
          lockStartTime: 0,
          lockEndTime: 0,
          userBoostedShare: 0,
          floatieAtLastUserAction: 0,
          locked: false,
          lockedAmount: 0
        }, // userInfo,
        earned: 0, // user.shares * pricePerFullShare() - user.floatieAtLastUserAction - user.userBoostedShare - calculateOverdueFee() - calculatePerformanceFee()
        token: {
          address: this.$config.periodToken,
          symbol: this.$config.floatieSymbol,
          name: this.$config.floatieName,
          logoURI: '/img/new/floatie_item.svg'
        },
        stakeAmount: 0,
        loadingStake: false,
        apy: -1,
        apr: -1
      }
      try {
        const userAddress = await this.$getUserAddress()
        const VaultFile = await import('~/assets/abis/FloatieVault.json')
        const Vault = VaultFile.default
        const vaultContract = new window.eth.eth.Contract(Vault, this.$config.periodVault)
        answer.performanceFee = await vaultContract.methods.performanceFee().call({ from: userAddress })
        answer.performanceFee = parseInt(answer.performanceFee, 10) / 100
        answer.totalLocked = await vaultContract.methods.totalLockedAmount().call({ from: userAddress })
        answer.totalLocked = parseFloat(window.eth.utils.fromWei(answer.totalLocked))
        answer.totalStaked = await vaultContract.methods.available().call({ from: userAddress })
        answer.totalStaked = parseFloat(window.eth.utils.fromWei(answer.totalStaked))
        answer.withdrawFee = await vaultContract.methods.withdrawFee().call({ from: userAddress })
        answer.withdrawFee = parseInt(answer.withdrawFee, 10) / 100
        answer.withdrawFeePeriod = await vaultContract.methods.withdrawFeePeriod().call({ from: userAddress })
        answer.withdrawFeePeriod = parseInt(answer.withdrawFeePeriod, 10) / 60 / 60 / 24
        answer.info = await vaultContract.methods.userInfo(userAddress).call({ from: userAddress })
        answer.info.lockedAmount = parseFloat(window.eth.utils.fromWei(answer.info.lockedAmount.toString()))
        answer.pricePerFullShare = await vaultContract.methods.getPricePerFullShare().call({ from: userAddress })
        answer.userOverdueFee = await vaultContract.methods.calculateOverdueFee(userAddress).call({ from: userAddress })
        answer.userPerformanceFee = await vaultContract.methods.calculatePerformanceFee(userAddress).call({ from: userAddress })
        answer.earned = parseFloat(window.eth.utils.fromWei(answer.info.shares.toString())) *
          parseFloat(window.eth.utils.fromWei(answer.pricePerFullShare)) -
          parseFloat(window.eth.utils.fromWei(answer.info.floatieAtLastUserAction)) -
          parseFloat(window.eth.utils.fromWei(answer.info.userBoostedShare)) -
          parseFloat(window.eth.utils.fromWei(answer.userOverdueFee)) -
          parseFloat(window.eth.utils.fromWei(answer.userPerformanceFee))
        answer.info.shares = parseFloat(window.eth.utils.fromWei(answer.info.shares.toString()))
        answer.info.floatieAtLastUserAction = parseFloat(window.eth.utils.fromWei(answer.info.floatieAtLastUserAction.toString()))
        this.lockedPool = answer
        this.lockedPool.apr = this.$calculateApr(this.floatieToUsd, this.floatieToUsd, this.lockedPool.totalStaked, this.$config.floatiePerBlock)
        this.lockedPool.apy = this.$calculateApy(this.lockedPool.apr, 1, 365, this.lockedPool.performanceFee)
        this.lockedPoolLoading = false
      } catch (err) {
        console.log(err)
        this.lockedPoolLoading = false
      }
    },
    async loadFlexiblePool() {
      const chainId = await window.eth.eth.net.getId()
      if (!this.$isChainSupported(chainId)) {
        this.flexiblePoolLoading = false
        return
      }
      try {
        this.flexiblePoolLoading = true
        const answer = {
          type: 'flexible',
          opened: false,
          performanceFee: 0, // performanceFee
          totalLocked: 0, // totalLockedAmount
          totalStaked: 0, // available
          withdrawFee: 0, // withdrawFee
          withdrawFeePeriod: 0, // withdrawFeePeriod
          info: {
            shares: 0,
            floatieAtLastUserAction: 0,
            lastDepositedTime: 0,
            lastUserActionTime: 0,
            unstakeFee: 0
          }, // userInfo,
          earned: 0, // user.shares * pricePerFullShare() - user.floatieAtLastUserAction - user.userBoostedShare - calculateOverdueFee() - calculatePerformanceFee()
          token: {
            address: this.$config.periodToken,
            symbol: this.$config.floatieSymbol,
            name: this.$config.floatieName,
            logoURI: '/img/new/floatie_item.svg'
          },
          stakeAmount: 0,
          loadingStake: false,
          apy: -1,
          apr: -1
        }
        const userAddress = await this.$getUserAddress()
        const VaultFile = await import('~/assets/abis/FloatieFlexibleVault.json')
        const Vault = VaultFile.default
        const vaultContract = new window.eth.eth.Contract(Vault, this.$config.periodFlexibleVault)
        answer.performanceFee = await vaultContract.methods.performanceFee().call({ from: userAddress })
        answer.performanceFee = parseInt(answer.performanceFee, 10) / 100
        answer.totalLocked = await vaultContract.methods.balanceOf().call({ from: userAddress })
        answer.totalLocked = parseFloat(window.eth.utils.fromWei(answer.totalLocked))
        answer.totalShares = await vaultContract.methods.totalShares().call({ from: userAddress })
        answer.totalShares = parseFloat(window.eth.utils.fromWei(answer.totalShares))
        answer.withdrawFee = await vaultContract.methods.withdrawFee().call({ from: userAddress })
        answer.withdrawFee = parseInt(answer.withdrawFee, 10) / 100
        answer.withdrawFeePeriod = await vaultContract.methods.withdrawFeePeriod().call({ from: userAddress })
        answer.withdrawFeePeriod = parseInt(answer.withdrawFeePeriod, 10) / 60 / 60 / 24
        answer.info = await vaultContract.methods.userInfo(userAddress).call({ from: userAddress })
        answer.info.lockedAmount = parseFloat(window.eth.utils.fromWei(answer.info.floatieAtLastUserAction.toString()))
        answer.info.shares = parseFloat(window.eth.utils.fromWei(answer.info.shares.toString()))
        answer.earned = answer.info.shares *
        answer.totalLocked /
        answer.totalShares -
        answer.info.lockedAmount
        answer.pricePerFullShare = await vaultContract.methods.getPricePerFullShare().call({ from: userAddress })
        const nowDate = new Date()
        const dateFreeUnstake = new Date(parseInt(answer.info.lastDepositedTime, 10) * 1000)
        dateFreeUnstake.setDate(dateFreeUnstake.getDate() + answer.withdrawFeePeriod)
        answer.info.unstakeFee = nowDate < dateFreeUnstake && answer.info.lockedAmount > 0 ? answer.withdrawFee : 0
        this.flexiblePool = answer
        this.flexiblePool.apr = this.$calculateApr(this.floatieToUsd, this.floatieToUsd, this.flexiblePool.totalLocked, this.$config.floatiePerBlock)
        this.flexiblePool.apy = this.$calculateApy(this.flexiblePool.apr, 1, 365, this.flexiblePool.performanceFee)
        this.flexiblePoolLoading = false
      } catch (err) {
        console.log(err)
        this.flexiblePoolLoading = false
      }
    },
    unlocksRealDate() {
      const realDate = this.$realisticDate(parseInt(this.lockedPool.info.lockEndTime, 10) * 1000, true)
      return realDate !== '-' ? realDate : this.$t('stake.unlocked')
    },
    lockedPeriodEnded() {
      const expiredDate = new Date(this.lockedPool.info.lockEndTime * 1000)
      const nowDate = new Date()
      return nowDate > expiredDate
    }
  }
}
</script>

<style scoped>
@import url('~/assets/css/new/stake.css');
@import url('~/assets/css/new/filter.css');
</style>
